import {css, type CSSProperties} from "aphrodite";
import * as React from "react";
import {useContext} from "react";

import {addStyle, View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {PermissionsInfo} from "@khan/shared-core";
import {redirect, useLocation} from "@khan/urls";

import {Footer as DefaultFooter} from "#app-shell-package/footer.tsx";
import {Header as DefaultHeader} from "#app-shell-package/header.tsx";
import {
    getExtraInfoRedirectPath,
    useForceExtraInfo,
} from "#shared/auth/utils/force-extra-info.ts";

import PageTemplateContext from "../page-template-context.ts";

const LiveBadgeAwardModalLauncher = React.lazy(
    () => import("#skills-to-proficient/badges/award-modal-launcher.tsx"),
);

export type BasePageTemplateProps = {
    children: React.ReactNode;

    /* CSS styles for the container. */
    containerStyle?: StyleType;
    /* CSS styles for the main content. */
    mainContentStyle?: CSSProperties;
    /* CSS styles for the main content wrapper. */
    mainContentWrapperStyle?: CSSProperties;
    /* CSS styles for the inner container. */
    innerContainerStyle?: CSSProperties;

    /* Whether to include a header at the top of the page. Defaults to true.
       NOTE: When false, the header may briefly be visible. This is a known
       problem that is to be addressed when upgrading React Router. */
    showHeader?: boolean;
    /* A custom header component to render at the top of the page. */
    header?: React.ComponentType;

    /* Whether to include a footer at the bottom of the page. Defaults to true.
       NOTE: When false, the footer may briefly be visible. This is a known
       problem that is to be addressed when upgrading React Router. */
    showFooter?: boolean;
    /* A custom footer component to render at the bottom of the page. */
    footer?: React.ComponentType;

    /* A sidebar component to render alongside the main content. */
    sidebar?: React.ReactElement;
};

const StyledDiv = addStyle("div");

/**
 * A customizable component that can be used to render a page template,
 * including the site header, site footer, and shared styles and utilties.
 *
 * You probably don't want to use this component directly; instead, use
 * DefaultPageTemplate for the default experience.
 */
const BasePageTemplate = ({
    children,
    containerStyle,
    footer,
    header,
    innerContainerStyle,
    mainContentStyle,
    mainContentWrapperStyle,
    showFooter = true,
    showHeader = true,
    sidebar,
}: BasePageTemplateProps): React.ReactElement => {
    const location = useLocation();

    // When the feature is turned on, we'll be forcing users to provide extra
    // info before they can access the site. The data collected varies by role.
    const forceExtraInfo = useForceExtraInfo();
    if (forceExtraInfo) {
        redirect(getExtraInfoRedirectPath(location));
    }

    const {hasPageTemplate} = useContext(PageTemplateContext);
    if (hasPageTemplate) {
        // If there's already a template ancestor, then just render children
        return <>{children}</>;
    }

    const mainContent = (
        <main
            className={css(mainContentStyle ?? null)}
            id="main-content"
            tabIndex={-1}
        >
            {children}
        </main>
    );

    const Header = header ?? DefaultHeader;
    const Footer = footer ?? DefaultFooter;
    if (sidebar) {
        // If a sidebar is provided, render the sidebar alongside the main content
        return (
            <PageTemplateContext.Provider value={{hasPageTemplate: true}}>
                {PermissionsInfo.isLoggedIn && <LiveBadgeAwardModalLauncher />}
                <StyledDiv style={containerStyle}>
                    {showHeader && <Header />}
                    <View style={innerContainerStyle}>
                        <View style={mainContentWrapperStyle}>
                            {mainContent}
                            {showFooter && <Footer />}
                        </View>
                        {sidebar}
                    </View>
                </StyledDiv>
            </PageTemplateContext.Provider>
        );
    }

    return (
        <PageTemplateContext.Provider value={{hasPageTemplate: true}}>
            {PermissionsInfo.isLoggedIn && (
                <React.Suspense fallback={null}>
                    <LiveBadgeAwardModalLauncher />
                </React.Suspense>
            )}
            <StyledDiv style={containerStyle}>
                {showHeader && <Header />}
                {mainContent}
                {showFooter && <Footer />}
            </StyledDiv>
        </PageTemplateContext.Provider>
    );
};

export default BasePageTemplate;
